import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

// deep import (not the feature barrel) to avoid a trading<->custodial import cycle.
import { useCustodialPanelState } from '@/features/custodial/hooks/useCustodialPanelState';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';

// Cancelled is terminal like the rest: a limit order that was dropped before it
// ever filled. It belongs in history, never in the active list.
const CLOSED_STATUSES =
    'Closed,TakeProfit,StopLoss,Liquidated,Failed,Cancelled';

const HISTORY_LIMIT = 20;
const PENDING_POLL_INTERVAL_MS = 2_000;
const PENDING_POLL_TIMEOUT_MS = 30_000;

/**
 * Fetches the authenticated user's closed trade history from the API.
 *
 * Only enabled in real mode with a valid auth token. Returns an empty
 * array while disabled so callers don't need null-checks.
 *
 * When `pendingClosedTradeHash` is set in the store (after a close tx is
 * mined), the query polls every 2 s until that trade appears in the
 * results — ensuring the terminal shows the latest closed trade.
 * A 30 s safety timeout auto-clears the flag if the backend never reports it.
 */
export function useUserTrades() {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const token = useTradingStore((s) => s.token);
    const pendingHash = useTradingStore((s) => s.pendingClosedTradeHash);
    const networkId = useTokenPairStore((s) => s.networkId);
    // trades history moved to factions.backend's speed facet (read-only, V1).
    const backend = useSpeedBackend();
    // pause background polling while the Quick Trade Wallet modal is open.
    const custodialPanelOpen = useCustodialPanelState((s) => s.isOpen);

    const enabled =
        isRealMode && !!token && backend !== null && networkId !== null;

    const result = useQuery({
        queryKey: queryKeys.trades.history(networkId ?? 0),
        queryFn: async () => {
            if (!backend || networkId === null) return [];
            return backend.fetchUserTrades({
                networkId,
                status: CLOSED_STATUSES,
                limit: HISTORY_LIMIT,
            });
        },
        enabled,
        staleTime: 30_000,
        refetchInterval:
            pendingHash && !custodialPanelOpen
                ? PENDING_POLL_INTERVAL_MS
                : false,
    });

    // Clear the pending flag once the trade appears in the fetched data.
    const clearedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!pendingHash || clearedRef.current === pendingHash) return;
        if (result.data?.some((t) => t.tradeHash === pendingHash)) {
            clearedRef.current = pendingHash;
            useTradingStore.getState().setPendingClosedTradeHash(null);
        }
    }, [pendingHash, result.data]);

    // Safety timeout: auto-clear after 30 s to avoid infinite polling.
    useEffect(() => {
        if (!pendingHash) return;
        clearedRef.current = null;
        const timer = setTimeout(() => {
            useTradingStore.getState().setPendingClosedTradeHash(null);
        }, PENDING_POLL_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [pendingHash]);

    return result;
}
